import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDocument, useFirestoreMutations } from '../../hooks/useFirestore'
import './AdminEditor.css'

const EMPTY_CIPHER = {
  name: '',
  description: '',
  substitutions: {},
  characterCipher: null,
  sortOrder: 0
}

const DEFAULT_VOWEL_ROTATION = ['A', 'E', 'I', 'O', 'U', 'Y']

export default function CipherEditor() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isNew = id === 'new'

  const { data: existingCipher, loading } = useDocument('ciphers', isNew ? null : id)
  const { add, update, remove } = useFirestoreMutations('ciphers')

  const [cipher, setCipher] = useState(EMPTY_CIPHER)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  // Character cipher toggle
  const [useCharCipher, setUseCharCipher] = useState(false)
  const [consonantShift, setConsonantShift] = useState(2)
  const [vowelRotation, setVowelRotation] = useState(DEFAULT_VOWEL_ROTATION.join(', '))

  // Substitution editing
  const [substitutionsList, setSubstitutionsList] = useState([])
  const [newPlain, setNewPlain] = useState('')
  const [newCipher, setNewCipher] = useState('')
  const [newCanonical, setNewCanonical] = useState(false)

  useEffect(() => {
    if (existingCipher) {
      setCipher({ ...EMPTY_CIPHER, ...existingCipher })

      // Parse character cipher
      const charCfg = existingCipher.characterCipher || existingCipher.character_cipher
      if (charCfg) {
        setUseCharCipher(true)
        setConsonantShift(charCfg.consonantShift || charCfg.consonant_shift || 2)
        const vowels = charCfg.vowelRotation || charCfg.vowel_rotation || DEFAULT_VOWEL_ROTATION
        setVowelRotation(vowels.join(', '))
      }

      // Parse substitutions into list
      const subs = existingCipher.substitutions || {}
      const list = Object.entries(subs).map(([plain, val]) => {
        if (typeof val === 'object' && val !== null) {
          return { plain, cipher: val.cipher, canonical: val.canonical || false }
        }
        return { plain, cipher: val, canonical: false }
      })
      setSubstitutionsList(list.sort((a, b) => a.plain.localeCompare(b.plain)))
    }
  }, [existingCipher])

  const handleChange = (field, value) => {
    setCipher(prev => ({ ...prev, [field]: value }))
  }

  const addSubstitution = () => {
    if (!newPlain.trim() || !newCipher.trim()) return

    // Check for duplicates
    if (substitutionsList.some(s => s.plain.toLowerCase() === newPlain.toLowerCase())) {
      setError(`"${newPlain}" already exists in substitutions`)
      return
    }

    setSubstitutionsList(prev => [...prev, {
      plain: newPlain.trim(),
      cipher: newCipher.trim(),
      canonical: newCanonical
    }].sort((a, b) => a.plain.localeCompare(b.plain)))

    setNewPlain('')
    setNewCipher('')
    setNewCanonical(false)
    setError(null)
  }

  const removeSubstitution = (plain) => {
    setSubstitutionsList(prev => prev.filter(s => s.plain !== plain))
  }

  const toggleCanonical = (plain) => {
    setSubstitutionsList(prev => prev.map(s =>
      s.plain === plain ? { ...s, canonical: !s.canonical } : s
    ))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError(null)

    try {
      // Build substitutions object
      const substitutions = {}
      for (const sub of substitutionsList) {
        if (sub.canonical) {
          substitutions[sub.plain] = { cipher: sub.cipher, canonical: true }
        } else {
          substitutions[sub.plain] = sub.cipher
        }
      }

      // Build character cipher config
      let characterCipher = null
      if (useCharCipher) {
        const vowels = vowelRotation.split(',').map(v => v.trim().toUpperCase()).filter(Boolean)
        characterCipher = {
          consonantShift: parseInt(consonantShift) || 2,
          vowelRotation: vowels.length > 0 ? vowels : DEFAULT_VOWEL_ROTATION
        }
      }

      const data = {
        name: cipher.name,
        description: cipher.description,
        substitutions,
        characterCipher,
        sortOrder: cipher.sortOrder
      }

      if (isNew) {
        await add(data)
      } else {
        await update(id, data)
      }

      navigate('/admin/ciphers')
    } catch (err) {
      setError(err.message)
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${cipher.name}"? This cannot be undone.`)) {
      return
    }

    setSaving(true)
    try {
      await remove(id)
      navigate('/admin/ciphers')
    } catch (err) {
      setError(err.message)
      setSaving(false)
    }
  }

  const handleImportJson = async () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.json'
    input.onchange = async (e) => {
      const file = e.target.files[0]
      if (!file) return

      try {
        const text = await file.text()
        const json = JSON.parse(text)

        // Set basic fields
        if (json.name) handleChange('name', json.name)
        if (json.description) handleChange('description', json.description)

        // Parse character cipher
        const charCfg = json.character_cipher || json.characterCipher
        if (charCfg) {
          setUseCharCipher(true)
          setConsonantShift(charCfg.consonant_shift || charCfg.consonantShift || 2)
          const vowels = charCfg.vowel_rotation || charCfg.vowelRotation || DEFAULT_VOWEL_ROTATION
          setVowelRotation(vowels.join(', '))
        } else {
          setUseCharCipher(false)
        }

        // Parse substitutions
        const subs = json.substitutions || {}
        const list = Object.entries(subs).map(([plain, val]) => {
          if (typeof val === 'object' && val !== null) {
            return { plain, cipher: val.cipher, canonical: val.canonical || false }
          }
          return { plain, cipher: val, canonical: false }
        })
        setSubstitutionsList(list.sort((a, b) => a.plain.localeCompare(b.plain)))

        setError(null)
      } catch (err) {
        setError('Failed to parse JSON file: ' + err.message)
      }
    }
    input.click()
  }

  if (loading) {
    return (
      <div className="container">
        <div className="loading-spinner" />
      </div>
    )
  }

  return (
    <div className="container">
      <div className="editor-header">
        <h1>{isNew ? 'New Cipher' : 'Edit Cipher'}</h1>
        <div className="header-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleImportJson}
          >
            Import JSON
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => navigate('/admin/ciphers')}
          >
            Back
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="editor-form">
        {error && <div className="error-banner">{error}</div>}

        <div className="form-group">
          <label htmlFor="name">Name *</label>
          <input
            id="name"
            type="text"
            value={cipher.name}
            onChange={(e) => handleChange('name', e.target.value)}
            required
            placeholder="Cipher name"
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            rows={2}
            value={cipher.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Brief description of this cipher..."
          />
        </div>

        <div className="form-group">
          <label htmlFor="sortOrder">Sort Order</label>
          <input
            id="sortOrder"
            type="number"
            value={cipher.sortOrder}
            onChange={(e) => handleChange('sortOrder', parseInt(e.target.value) || 0)}
            style={{ maxWidth: '100px' }}
          />
        </div>

        <fieldset className="form-fieldset">
          <legend>Character Cipher</legend>

          <div className="form-group form-group-inline">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={useCharCipher}
                onChange={(e) => setUseCharCipher(e.target.checked)}
              />
              <span>Enable character-level cipher</span>
            </label>
          </div>

          {useCharCipher && (
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="consonantShift">Consonant Shift</label>
                <input
                  id="consonantShift"
                  type="number"
                  min="1"
                  max="25"
                  value={consonantShift}
                  onChange={(e) => setConsonantShift(e.target.value)}
                  style={{ maxWidth: '80px' }}
                />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label htmlFor="vowelRotation">Vowel Rotation</label>
                <input
                  id="vowelRotation"
                  type="text"
                  value={vowelRotation}
                  onChange={(e) => setVowelRotation(e.target.value)}
                  placeholder="A, E, I, O, U, Y"
                />
                <p className="help-text">Comma-separated vowels in rotation order</p>
              </div>
            </div>
          )}
        </fieldset>

        <fieldset className="form-fieldset">
          <legend>Substitutions ({substitutionsList.length})</legend>

          <div className="substitution-add">
            <input
              type="text"
              value={newPlain}
              onChange={(e) => setNewPlain(e.target.value)}
              placeholder="Plain text"
              style={{ flex: 1 }}
            />
            <input
              type="text"
              value={newCipher}
              onChange={(e) => setNewCipher(e.target.value)}
              placeholder="Cipher text"
              style={{ flex: 1 }}
            />
            <label className="checkbox-label compact">
              <input
                type="checkbox"
                checked={newCanonical}
                onChange={(e) => setNewCanonical(e.target.checked)}
              />
              <span>Canon</span>
            </label>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={addSubstitution}
            >
              Add
            </button>
          </div>

          {substitutionsList.length > 0 && (
            <div className="substitution-list">
              <div className="substitution-header">
                <span>Plain</span>
                <span>Cipher</span>
                <span>Canon</span>
                <span></span>
              </div>
              {substitutionsList.map(sub => (
                <div key={sub.plain} className="substitution-row">
                  <span className="sub-plain">{sub.plain}</span>
                  <span className="sub-cipher">{sub.cipher}</span>
                  <span className="sub-canonical">
                    <input
                      type="checkbox"
                      checked={sub.canonical}
                      onChange={() => toggleCanonical(sub.plain)}
                    />
                  </span>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => removeSubstitution(sub.plain)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </fieldset>

        <div className="form-actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving}
          >
            {saving ? 'Saving...' : (isNew ? 'Create Cipher' : 'Save Changes')}
          </button>

          {!isNew && (
            <button
              type="button"
              className="btn btn-danger"
              onClick={handleDelete}
              disabled={saving}
            >
              Delete Cipher
            </button>
          )}
        </div>
      </form>

      <style>{`
        .header-actions {
          display: flex;
          gap: var(--space-sm);
        }
        .form-fieldset {
          border: 1px solid var(--ashen-char);
          border-radius: var(--radius-md);
          padding: var(--space-lg);
          margin-bottom: var(--space-lg);
        }
        .form-fieldset legend {
          padding: 0 var(--space-sm);
          font-weight: 600;
          color: var(--color-text-heading);
        }
        .substitution-add {
          display: flex;
          gap: var(--space-sm);
          align-items: center;
          margin-bottom: var(--space-md);
        }
        .checkbox-label.compact {
          display: flex;
          align-items: center;
          gap: var(--space-xs);
          white-space: nowrap;
        }
        .substitution-list {
          border: 1px solid var(--ashen-char);
          border-radius: var(--radius-md);
          overflow: hidden;
          max-height: 400px;
          overflow-y: auto;
        }
        .substitution-header,
        .substitution-row {
          display: grid;
          grid-template-columns: 1fr 1fr 60px 40px;
          gap: var(--space-sm);
          padding: var(--space-sm) var(--space-md);
          align-items: center;
        }
        .substitution-header {
          background: var(--color-bg-elevated);
          font-weight: 600;
          font-size: 0.8rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: var(--color-text-secondary);
          position: sticky;
          top: 0;
        }
        .substitution-row {
          border-top: 1px solid var(--ashen-char);
        }
        .substitution-row:hover {
          background: var(--color-bg-elevated);
        }
        .sub-plain {
          font-weight: 500;
        }
        .sub-cipher {
          color: var(--ember-violet);
        }
        .sub-canonical {
          text-align: center;
        }
      `}</style>
    </div>
  )
}
